/* 루미나 화면 층 신작 — 블랙 HomePage.jsx BlackGameSlideRow(신규게임 행)의 데이터·클릭·빈 목록 규칙 그대로, 표현만 시안 "원호 캐러셀"로.
   시안 실측: Swiper autoplay delay 0 · speed 8000 · loop · centeredSlides · 카드 342 · 간격 24 → 8초에 한 칸(366px)씩 끊김 없이
   왼쪽으로 흐르고 호버해도 멈추지 않는다. 드래그로 밀 수 있고 놓으면 이어서 흐른다. 기울기는 매 프레임 화면 중앙과의 거리로
   다시 계산한다(칸당 -5.8°, 위로 11.8·d²). 드래그 직후의 클릭은 1회 무시(블랙 규약: 실행 오발동 방지). 움직임 최소화 설정이면 자동 이동만 끈다.
   WHY: SPEC §2 — 게임 목록(newGames)·클릭 실행(부모 onCardClick → 로그인 시 GamePopup, 비로그인 /login)은 부모 로직 그대로. */
import React, { useEffect, useRef } from 'react';
import { IconBtn, gameThumbSrc, gameDisplayName, gameVendorLabel, gameTypeLabel, onThumbError, prefersReducedMotion, onEnterOrSpace } from './homeShared';
import { DecorBubbles } from './LuminaDecor';

const SPEED = 366 / 8000;   // px/ms — 한 칸(342+24px) 이동에 8000ms
const GAP = 24;
const MIN_CARDS = 8;        // 무한 순환이 비지 않도록 목록을 반복해 최소 8장을 채운다(같은 게임을 다시 그릴 뿐, 데이터 추가 없음)

/* desc: 제목 아래 설명 한 줄(관리자 '홈 문구 > 신규게임', 시안 .sec-head .sub) — 비면 그리지 않는다
   decorBubble: 방울 장식 그림(관리자 '장식 그림 > 방울', 참고 사이트 신규게임 구간 방울 2개 자리) — 비면 없음 */
const FeaturedArc = ({ games, title, desc, decorBubble, onCardClick }) => {
  const sliderRef = useRef(null);
  const cardRefs = useRef([]);
  const dotRefs = useRef([]);
  const st = useRef({ pos: 0, dragDx: 0, drag: null, moved: false, last: null, raf: 0, jumpTo: null, step: null, onMove: null, onUp: null });

  const base = games.length;
  const reps = Math.max(1, Math.ceil(MIN_CARDS / Math.max(1, base)));
  const items = [];
  for (let r = 0; r < reps; r++) games.forEach((game, i) => items.push({ game, key: `${game.id}-${r}`, baseIndex: i }));
  const n = items.length;

  useEffect(() => {
    const s = st.current;
    const reduce = prefersReducedMotion();
    const pitch = () => ((cardRefs.current[0] && cardRefs.current[0].offsetWidth) || 342) + GAP;
    const layout = () => {
      const P = pitch(); const total = n * P; const off = s.pos - s.dragDx;
      let nearest = -1; let nearestAbs = 1e9;
      for (let i = 0; i < n; i++) {
        const el = cardRefs.current[i];
        if (!el) continue;
        let x = i * P - off;                                        // 카드 중앙의 화면 중앙 기준 위치
        x = ((x % total) + total + total / 2) % total - total / 2;  // 무한 순환: 항상 [-total/2, total/2)
        const dd = x / P; const y = -11.8 * dd * dd; const rot = -5.8 * dd;
        el.style.transform = `translate(${x.toFixed(1)}px,${y.toFixed(1)}px) rotate(${rot.toFixed(2)}deg)`;
        el.style.zIndex = String(40 - Math.round(Math.abs(dd)));
        el.style.opacity = Math.abs(dd) > 3.6 ? '0' : '1';
        if (Math.abs(x) < nearestAbs) { nearestAbs = Math.abs(x); nearest = i; }
      }
      const on = nearest >= 0 && base > 0 ? nearest % base : -1;
      dotRefs.current.forEach((d, j) => { if (d) d.classList.toggle('on', j === on); });
    };
    const tick = (now) => {
      if (!reduce && !s.drag && s.last != null) s.pos += (now - s.last) * SPEED;   // 연속 자동 이동
      s.last = now; layout();
      s.raf = requestAnimationFrame(tick);
    };
    s.jumpTo = (j) => { s.pos = j * pitch(); layout(); };
    s.step = (dir) => { s.pos += dir * pitch(); };
    s.last = null;
    s.raf = requestAnimationFrame(tick);
    window.addEventListener('resize', layout);
    return () => {
      cancelAnimationFrame(s.raf);
      window.removeEventListener('resize', layout);
      /* 드래그 도중 언마운트되어도 창 리스너가 남지 않게 */
      if (s.onMove) window.removeEventListener('pointermove', s.onMove);
      if (s.onUp) { window.removeEventListener('pointerup', s.onUp); window.removeEventListener('pointercancel', s.onUp); }
      s.drag = null; s.dragDx = 0; s.jumpTo = null; s.step = null;
    };
  }, [n, base]);

  /* 드래그: pointerdown 은 슬라이더에서, 이동·놓기는 창 전체에서 받는다(카드 클릭 이벤트를 살리기 위해 포인터 캡처는 쓰지 않는다) */
  const onPointerDown = (e) => {
    const s = st.current;
    if (e.button != null && e.button !== 0) return;
    s.drag = { x: e.clientX }; s.moved = false; s.dragDx = 0;
    if (sliderRef.current) sliderRef.current.classList.add('dragging');
    s.onMove = (ev) => {
      if (!s.drag) return;
      s.dragDx = ev.clientX - s.drag.x;
      if (Math.abs(s.dragDx) > 4) s.moved = true;
    };
    s.onUp = () => {
      if (!s.drag) return;
      s.pos -= s.dragDx; s.dragDx = 0; s.drag = null;
      if (sliderRef.current) sliderRef.current.classList.remove('dragging');
      window.removeEventListener('pointermove', s.onMove);
      window.removeEventListener('pointerup', s.onUp);
      window.removeEventListener('pointercancel', s.onUp);
      s.onMove = null; s.onUp = null;
    };
    window.addEventListener('pointermove', s.onMove);
    window.addEventListener('pointerup', s.onUp);
    window.addEventListener('pointercancel', s.onUp);
  };
  const onKeyDown = (e) => {
    const s = st.current;
    if (e.key === 'ArrowRight' && s.step) s.step(1);
    if (e.key === 'ArrowLeft' && s.step) s.step(-1);
  };
  /* 드래그로 움직인 직후의 클릭은 1회 무시(블랙 규약) */
  const handleCardClick = (game) => {
    const s = st.current;
    if (s.moved) { s.moved = false; return; }
    onCardClick(game);
  };

  if (!base) return null;

  return (
    <section className="featured" data-block="black-new-games">
      <DecorBubbles bubble={decorBubble} variant="featured" />
      <div className="container">
        <div className="sec-head">
          <div className="tt">
            <h2 className="heading-2"><span>{title}</span></h2>
            {desc && <p className="sub">{desc}</p>}
          </div>
        </div>
      </div>
      <div className="fslider" ref={sliderRef} tabIndex={0} aria-label={`${title} 캐러셀`} onPointerDown={onPointerDown} onKeyDown={onKeyDown}>
        <div className="ftrack">
          {items.map(({ game, key }, i) => {
            const name = gameDisplayName(game);
            const tags = [gameVendorLabel(game), gameTypeLabel(game)].filter(Boolean);
            return (
              <article
                key={key}
                className="gcard group"
                role="button"
                tabIndex={0}
                ref={(el) => { cardRefs.current[i] = el; }}
                onClick={() => handleCardClick(game)}
                onKeyDown={onEnterOrSpace(() => onCardClick(game))}
              >
                <div className="top">{tags.map((t) => <span className="tag" key={t}>{t}</span>)}</div>
                <div className="im">
                  <img src={gameThumbSrc(game)} alt={name} loading="lazy" draggable={false} onError={onThumbError} />
                </div>
                <div className="bottom"><h3>{name}</h3><IconBtn /></div>
              </article>
            );
          })}
        </div>
      </div>
      <div className="fdots">
        {games.map((game, j) => (
          <i key={game.id} ref={(el) => { dotRefs.current[j] = el; }} role="button" tabIndex={0} aria-label={`${j + 1}`} onClick={() => st.current.jumpTo && st.current.jumpTo(j)} onKeyDown={onEnterOrSpace(() => st.current.jumpTo && st.current.jumpTo(j))} />
        ))}
      </div>
    </section>
  );
};

export default FeaturedArc;
